import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const content = {
  pt: {
    title: "Pagamento Confirmado!",
    subtitle: "Seu pagamento foi processado com sucesso.",
    processing: "Verificando pagamento...",
    details: "Detalhes do pagamento",
    package: "Plano",
    amount: "Valor",
    status: "Status",
    paid: "Pago",
    pending: "Processando",
    nextSteps: "Próximos passos",
    step1: "Seu canal de compliance já está ativo",
    step2: "Acesse o painel administrativo para gerenciar denúncias e ouvidoria",
    step3: "Configure as preferências da sua empresa",
    goToDashboard: "Ir para o painel",
    backHome: "Voltar ao início"
  },
  en: {
    title: "Payment Confirmed!",
    subtitle: "Your payment has been successfully processed.",
    processing: "Verifying payment...",
    details: "Payment details",
    package: "Plan",
    amount: "Amount",
    status: "Status",
    paid: "Paid",
    pending: "Processing",
    nextSteps: "Next steps",
    step1: "Your compliance channel is now active",
    step2: "Access the admin panel to manage reports and ombudsman",
    step3: "Configure your company preferences",
    goToDashboard: "Go to dashboard",
    backHome: "Back to home"
  }
};

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const currentLang = i18n.language || 'pt';
  const t = content[currentLang] || content.pt;
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setError("No session ID found");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/payments/status/${sessionId}`);
        setPaymentData(response.data);

        // If payment is still pending and we haven't polled too many times, poll again
        if (response.data.payment_status !== 'paid' && pollCount < 5) {
          setTimeout(() => {
            setPollCount(prev => prev + 1);
          }, 2000);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [sessionId, pollCount]);

  const formatAmount = (amount, currency) => {
    if (!amount) return '';
    const value = amount / 100; // Stripe returns cents
    if (currency === 'usd') {
      return `US$ ${value.toFixed(2)}`;
    }
    return `R$ ${value.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#f5a623] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">{t.processing}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-2">{t.title}</h1>
          <p className="text-slate-400 mb-8">{t.subtitle}</p>

          {/* Payment Details */}
          {paymentData && (
            <div className="bg-white/5 rounded-xl p-6 mb-8 text-left">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">
                {t.details}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-400">{t.package}</span>
                  <span className="text-white font-medium">
                    {paymentData.metadata?.package_name || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">{t.amount}</span>
                  <span className="text-white font-medium">
                    {formatAmount(paymentData.amount_total, paymentData.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">{t.status}</span>
                  <span className={`font-medium ${paymentData.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                    {paymentData.payment_status === 'paid' ? t.paid : t.pending}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="text-left mb-8">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4">
              {t.nextSteps}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-slate-300">
                <span className="w-6 h-6 bg-[#f5a623]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#f5a623] text-sm font-medium">1</span>
                <span>{t.step1}</span>
              </li>
              <li className="flex items-start gap-3 text-slate-300">
                <span className="w-6 h-6 bg-[#f5a623]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#f5a623] text-sm font-medium">2</span>
                <span>{t.step2}</span>
              </li>
              <li className="flex items-start gap-3 text-slate-300">
                <span className="w-6 h-6 bg-[#f5a623]/20 rounded-full flex items-center justify-center flex-shrink-0 text-[#f5a623] text-sm font-medium">3</span>
                <span>{t.step3}</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Link to="/" className="block">
              <Button className="w-full bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] font-semibold">
                {t.backHome}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
